import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    image = "image",
    linkTitle = "linkTitle",
    linkURL = "linkURL",
    divisions = "divisions",
    bottomDescription = "bottomDescription",
    buttonColor = "buttonColor",
    buttonBackgroundColor = "buttonBackgroundColor",
    borderColor = "borderColor",
}

interface ProfileList {
    fields: {
        textarea: string;
        image: string;
        title: string;
        description: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.image]: string;
        [fields.linkTitle]: string;
        [fields.linkURL]: LinkFieldValue;
        [fields.bottomDescription]: string;
        [fields.buttonColor]: string;
        [fields.buttonBackgroundColor]: string;
        [fields.divisions]: ProfileList[];
        [fields.borderColor]: string;
    };
}

export interface HomepageNewLookingExpertStyles {
    container?: InjectableCss;
    imgwrapper?: InjectableCss;
    descriptionWrapper?: InjectableCss;
    title?: InjectableCss;
    description?: InjectableCss;
    link?: InjectableCss;
    imagetitleStyles?: InjectableCss;
    rightDescription?: InjectableCss;
    leftMainContainer?: InjectableCss;
    leftContainer?: InjectableCss;
    leftChildContainer?: InjectableCss;
}

export const homepagenewLookingExpertStyles: HomepageNewLookingExpertStyles = {
    leftMainContainer: {
        css: css`
            background-color: #f2f2f2;
            padding: 40px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    leftContainer: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
            margin-bottom: 16px;
            border: 4px solid #e0dfe0;
            padding: 16px;
            box-sizing: border-box;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                    `,
                    null,
                    null,
                    null,
                    null,
                ]);
            }}
            &:last-child {
                margin-bottom: 0px;
            }
        `,
    },
    leftChildContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        justify-content: center;
                        align-items: center;
                    `,
                    null,
                    null,
                    null,
                    null,
                ]);
            }}
        `,
    },
    container: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            padding-bottom: 104px;
            @media (min-width: 1801px) {
                padding-left: 20%;
                padding-right: 20%;
            }
            @media (min-width: 1401px) and (max-width: 1800px) {
                padding-left: 18%;
                padding-right: 18%;
            }
            @media (min-width: 992px) and (max-width: 1400px) {
                padding-left: 120px;
                padding-right: 120px;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        padding-bottom: 56px;
                    `,
                    css`
                        flex-direction: column;
                        padding-bottom: 56px;
                    `,
                    css`
                        padding-left: 25px;
                        padding-right: 25px;
                        padding-bottom: 78px;
                    `,
                    null,
                ]);
            }}
        `,
    },
    imgwrapper: {
        css: css`
            max-width: 106px;
            height: 100%;
            width: 100%;
            aspect-ratio: 1/1;
            margin-right: 16px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-right: 0px;
                    `,
                    null,
                    null,
                    null,
                ]);
            }}
            img {
                width: 100%;
                aspect-ratio: 1/1;
            }
        `,
    },

    title: {
        css: css`
            color: ${getProp("theme.typography.h3.color")};
            margin-bottom: 24px !important;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 16px !important;
                    `,
                    css`
                        margin-bottom: 16px !important;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
    description: {
        css: css`
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 16px;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    imagetitleStyles: {
        css: css`
            font-size: 14px;
            font-style: normal;
            font-weight: 600;
            line-height: 20px;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    link: {
        css: css`
            height: 48px;
            border-radius: 24px;
            padding: 12px 24px;
            font-weight: 600;
            font-size: 18px;
            line-height: 24px;
            margin-bottom: 16px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        height: 32px;
                        border-radius: 16px;
                        padding: 8px 16px;
                        font-size: 14px;
                        line-height: 16px;
                    `,
                    css`
                        height: 32px;
                        border-radius: 16px;
                        padding: 8px 16px;
                        font-size: 14px;
                        line-height: 16px;
                    `,
                    null,
                    null,
                ]);
            }}

            > span {
                color: #fff !important;
            }
        `,
    },
    rightDescription: {
        css: css`
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            color: ${getProp("theme.typography.p.color")};
            margin-bottom: 32px !important;
        `,
    },
};

const styles = homepagenewLookingExpertStyles;

export const CENNewLookingForExpert: FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.linkURL);
    const descriptionWrapper = {
        css: css`
            display: flex;
            flex-direction: column;
            flex-basis: 65%;
            border: 1px solid ${fields.borderColor};
            padding: 56px;
            @media (min-width: 1433px) and (max-width: 1500px) {
                width: 70%;
                flex-basis: 70%;
            }
            @media (min-width: 992px) and (max-width: 1159px) {
                width: 55%;
                flex-basis: 55%;
                padding: 40px;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 32px;
                        flex-basis: 100%;
                        width: 100%;
                    `,
                    css`
                        padding: 32px;
                        flex-basis: 100%;
                        width: 100%;
                    `,
                    css`
                        flex-basis: 58%;
                        width: 58%;
                        padding: 25px;
                    `,
                    null,
                ]);
            }}
        `,
    };
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftMainContainer}>
                {fields.divisions.map((item, index) => (
                    <StyledWrapper {...styles.leftContainer} key="">
                        <LazyImage src={item.fields.image} {...styles.imgwrapper} altText={item.fields.title} />
                        <StyledWrapper {...styles.leftChildContainer}>
                            <Typography as="p" {...styles.imagetitleStyles}>
                                {item.fields.title}
                            </Typography>
                            <Typography as="p" {...styles.description}>
                                {item.fields.description}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
            <StyledWrapper {...descriptionWrapper}>
                <Typography variant="h3" {...styles.title}>
                    {fields.title}
                </Typography>
                <Typography variant="p" {...styles.rightDescription}>
                    {fields.description}
                </Typography>
                <StyledWrapper>
                    <Link
                        data-test-selector="looking-expert-link"
                        href={url}
                        {...styles.link}
                        style={{ background: `${fields.buttonBackgroundColor}`, color: `${fields.buttonColor}` }}
                    >
                        {fields.linkTitle}
                    </Link>
                </StyledWrapper>
                <Typography as="p" {...styles.description}>
                    {fields.bottomDescription}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CENNewLookingForExpert,
    definition: {
        group: "Basic",
        displayName: "CEN New Looking For Expert",
        fieldDefinitions: [
            {
                name: fields.title,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.description,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.linkTitle,
                editorTemplate: "TextField",
                displayName: "Link title",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.linkURL,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                displayName: "Link URL",
            },
            {
                name: fields.borderColor,
                editorTemplate: "ColorPickerField",
                displayName: "Border Color",
                defaultValue: "#8f0446",
                fieldType: "General",
            },
            {
                fieldType: "Translatable",
                name: fields.bottomDescription,
                editorTemplate: "TextField",
                displayName: "Bottom Description",
                defaultValue: "Mon-Fri, 9am to 5pm CT",
            },
            {
                name: fields.buttonColor,
                editorTemplate: "ColorPickerField",
                displayName: "Button Color",
                defaultValue: "#ffffff",
                fieldType: "General",
            },
            {
                name: fields.buttonBackgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Button  Background Color",
                defaultValue: "#8f0446",
                fieldType: "General",
            },
            {
                name: fields.divisions,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "description",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
